import React from "react";
import Modal from './Modal.js'; 
import { Link } from 'react-router-dom';
import { FaTimes, FaShoppingCart, FaHeart, FaShareAlt, FaChevronRight } from 'react-icons/fa';
import { FiCheckCircle } from 'react-icons/fi';

const CompareSimilarItems = ({ product, similarProducts, onClose }) => {
  // Helper function to get product image URL from either JSON array or direct URL
  const getProductImageUrl = (productImage) => {
    if (!productImage) return '/api/placeholder/300/300';
    try {
      // Try parsing as JSON first (legacy format)
      const productImages = JSON.parse(productImage);
      return Array.isArray(productImages) ? productImages[0] : productImage;
    } catch {
      // If parsing fails, it's already a direct URL (new format)
      return productImage;
    }
  };

  // Validate and ensure similarProducts is an array
  const validSimilarProducts = Array.isArray(similarProducts) ? similarProducts : [];
  const hasValidData = product && validSimilarProducts.length > 0;
  
  const discount = 0.10;
  const discountedPrice = product?.price ? (product.price * (1 - discount)).toFixed(2) : '0.00';
  const firstImage = getProductImageUrl(product?.productImage);

  // Sample features for comparison - replace with your actual data
  const comparisonFeatures = [
    { name: "Brand", value: product.brand || "Generic" },
    { name: "Model", value: product.model || "Standard" },
    { name: "Color", value: product.color || "Black" },
    { name: "Warranty", value: product.warranty || "1 Year" },
    { name: "Weight", value: product.weight || "500g" },
  ];

  return (
    <Modal isVisible={true} onClose={onClose} className="max-w-6xl">
      <div className="bg-white rounded-lg overflow-hidden">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b border-gray-200 p-4 bg-gray-50">
          <h2 className="text-xl font-bold text-gray-800">Compare Similar Items</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Loading or No Data State */}
        {!hasValidData ? (
          <div className="p-8 text-center">
            {!product ? (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Product not found</h3>
                <p className="text-gray-500">Unable to load product information for comparison.</p>
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No similar items found</h3>
                <p className="text-gray-500">We couldn't find similar products to compare at this time.</p>
              </div>
            )}
            <button 
              onClick={onClose}
              className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded text-sm"
            >
              Close
            </button>
          </div>
        ) : (
          <>
        {/* Comparison Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 overflow-x-auto">
          {/* Current Product Column */}
          <div className="border-r border-gray-200 p-4">
            <div className="flex flex-col items-center mb-4">
              <div className="relative w-full">
                <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">
                  -{(discount * 100).toFixed(0)}%
                </div>
                <img
                  src={firstImage}
                  alt={product.productName || 'Product image'}
                  className="w-full h-48 object-contain mx-auto"
                  onError={(e) => {
                    e.target.src = '/api/placeholder/300/300';
                  }}
                />
              </div>
              <Link 
                to={`/product/${encodeURIComponent(product.productName || 'unknown')}/${product.productID || product.productId || 'unknown'}`}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 mt-2 line-clamp-2 text-center"
              >
                {product.productName || 'Unknown Product'}
              </Link>
              <div className={`text-xs mt-1 ${product.inStock ? 'text-green-600' : 'text-red-600'}`}>
                {product.inStock ? "In Stock" : "Out of Stock"}
              </div>
              <div className="flex items-center text-xs text-yellow-500 mt-1">
                ⭐{4.5} ({30000})
              </div>
              {product?.price && (
                <div className="text-xs text-gray-500 line-through mt-1">
                  Was KSH {Number(product.price).toLocaleString()}
                </div>
              )}
              <div className="text-lg font-bold text-gray-800 mt-1">
                KSH {Number(discountedPrice).toLocaleString()}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 mt-4">
              <button className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-800 py-2 px-4 rounded text-sm font-medium flex items-center justify-center">
                <FaShoppingCart className="mr-2" /> Add to Cart
              </button>
              <button className="w-full border border-gray-300 hover:bg-gray-100 text-gray-800 py-2 px-4 rounded text-sm font-medium flex items-center justify-center">
                <FaHeart className="mr-2 text-red-500" /> Add to List
              </button>
              <button className="w-full border border-gray-300 hover:bg-gray-100 text-gray-800 py-2 px-4 rounded text-sm font-medium flex items-center justify-center">
                <FaShareAlt className="mr-2 text-blue-500" /> Share
              </button>
            </div>

            {/* Product Highlights */}
            <div className="mt-6">
              <h3 className="text-sm font-bold text-gray-800 mb-2">Highlights</h3>
              <ul className="text-xs space-y-1">
                {product.productDescription?.split("\n").filter(Boolean).slice(0, 5).map((item, i) => (
                  <li key={i} className="flex items-start">
                    <FiCheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{item.trim()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Similar Products Columns */}
          {validSimilarProducts.slice(0, 3).map((similarProduct, index) => {
            const similarFirstImage = getProductImageUrl(similarProduct.imageUrl || similarProduct.productImage);
            const similarDiscountedPrice = similarProduct?.price ? (similarProduct.price * (1 - discount)).toFixed(2) : '0.00';

            return (
              <div 
                key={index} 
                className={`p-4 ${index < 2 ? 'border-r border-gray-200' : ''}`}
              >
                <div className="flex flex-col items-center mb-4">
                  <div className="relative w-full">
                    {similarProduct?.price && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded z-10">
                        -{(discount * 100).toFixed(0)}%
                      </div>
                    )}
                    <img
                      src={similarFirstImage}
                      alt={similarProduct?.productName || 'Similar Product'}
                      className="w-full h-48 object-contain mx-auto bg-white rounded border"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/300/300';
                      }}
                    />
                  </div>
                  <Link 
                    to={`/product/${encodeURIComponent(similarProduct.productName || 'unknown')}/${similarProduct.productID || similarProduct.productId || 'unknown'}`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 mt-2 line-clamp-3 text-center"
                  >
                    {similarProduct.productName || 'Unknown Product'}
                  </Link>
                  <div className={`text-xs mt-1 ${similarProduct.inStock ? 'text-green-600' : 'text-red-600'}`}>
                    {similarProduct.inStock ? "In Stock" : "Out of Stock"}
                  </div>
                  <div className="flex items-center text-xs text-yellow-500 mt-1">
                    ⭐{similarProduct.rating || 4.0} ({similarProduct.reviews || 1000})
                  </div>
                  {similarProduct?.price && (
                    <div className="text-xs text-gray-500 line-through mt-1">
                      Was KSH {Number(similarProduct.price).toLocaleString()}
                    </div>
                  )}
                  <div className="text-lg font-bold text-gray-800 mt-1">
                    KSH {Number(similarDiscountedPrice).toLocaleString()}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2 mt-4">
                  <button className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-800 py-2 px-4 rounded text-sm font-medium flex items-center justify-center">
                    <FaShoppingCart className="mr-2" /> Add to Cart
                  </button>
                  <button className="w-full border border-gray-300 hover:bg-gray-100 text-gray-800 py-2 px-4 rounded text-sm font-medium flex items-center justify-center">
                    <FaHeart className="mr-2 text-red-500" /> Add to List
                  </button>
                  <button className="w-full border border-gray-300 hover:bg-gray-100 text-gray-800 py-2 px-4 rounded text-sm font-medium flex items-center justify-center">
                    <FaShareAlt className="mr-2 text-blue-500" /> Share
                  </button>
                </div>

                {/* Product Highlights */}
                <div className="mt-6">
                  <h3 className="text-sm font-bold text-gray-800 mb-2">Highlights</h3>
                  <ul className="text-xs space-y-1">
                    {similarProduct.productDescription?.split("\n").filter(Boolean).slice(0, 5).map((item, i) => (
                      <li key={i} className="flex items-start">
                        <FiCheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* Feature Comparison Table */}
        {/* Feature Comparison Table */}
        <div className="border-t border-gray-200 overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-gray-500 min-w-[100px]">Features</th>
                <th className="text-center p-3 text-sm font-medium text-gray-500 min-w-[150px]">
                  <div className="flex flex-col items-center">
                    <span>Selected Item</span>
                    <span className="text-xs text-yellow-600 font-normal">{product?.productName?.slice(0, 20)}...</span>
                  </div>
                </th>
                {validSimilarProducts.slice(0, 3).map((prod, index) => (
                  <th key={index} className="text-center p-3 text-sm font-medium text-gray-500 min-w-[150px]">
                    <div className="flex flex-col items-center">
                      <span>Option {index + 1}</span>
                      <span className="text-xs text-gray-600 font-normal">{prod?.productName?.slice(0, 20)}...</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {comparisonFeatures.map((feature, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="p-3 text-sm font-medium text-gray-900">{feature.name}</td>
                  <td className="p-3 text-sm text-center text-yellow-700 font-medium">{feature.value}</td>
                  {validSimilarProducts.slice(0, 3).map((similarProduct, j) => (
                    <td key={j} className="p-3 text-sm text-center">
                      {similarProduct?.[feature.name.toLowerCase()] || similarProduct?.brand || feature.value}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Price Comparison Row */}
              <tr className="bg-yellow-50">
                <td className="p-3 text-sm font-bold text-gray-900">Price</td>
                <td className="p-3 text-sm text-center font-bold text-yellow-700">
                  KSH {product?.price ? Number(discountedPrice).toLocaleString() : 'N/A'}
                </td>
                {validSimilarProducts.slice(0, 3).map((similarProduct, j) => {
                  const simPrice = similarProduct?.price ? (similarProduct.price * (1 - discount)).toFixed(2) : '0.00';
                  return (
                    <td key={j} className="p-3 text-sm text-center font-bold text-gray-700">
                      {similarProduct?.price ? `KSH ${Number(simPrice).toLocaleString()}` : 'N/A'}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

          </>
        )}

        {/* Footer */}
        <div className="bg-gray-50 p-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-sm text-gray-600">
            {hasValidData && (
              <span>Comparing with {validSimilarProducts.length} similar item{validSimilarProducts.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100"
            >
              Close Comparison
            </button>
            {hasValidData && (
              <button className="flex items-center px-4 py-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 rounded hover:bg-blue-100">
                See more similar items <FaChevronRight className="ml-1" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CompareSimilarItems;